<template>
  <div
    class="file-preview-tile flex"
    @contextmenu.prevent
    @click.right="allowContextMenu && (contextMenuOpen = true)"
    v-on-long-press="() => allowContextMenu && (contextMenuOpen = true)"
    @mouseenter="isMouseOver = true"
    @mouseleave="isMouseOver = false"
  >
    <div
      ref="draggableElement"
      draggable="true"
      @dragstart="onDragStart"
      @dragend="onDragEnd"
      @touchstart="onTouchStart"
      @touchend="onTouchEnd"
      class="surface flex w-full cursor-pointer rounded-md border-2 select-none hover:opacity-75"
      :class="{
        'border-blue-500 text-blue-600!': modelValue?.file.isWordDocument(),
        'border-green-500 text-green-600!': modelValue?.file.isSpreadsheet(),
        'border-red-400 text-red-500!': modelValue?.file.isPdfDocument()
      }"
    >
      <template v-if="!modelValue">
        <div class="flex h-full w-full items-center justify-center">
          <LoadingSpinner class="size-6" />
        </div>
      </template>
      <template
        v-else-if="(modelValue.file.isImage() || modelValue.file.isPdfDocument()) && !hasError"
      >
        <div v-if="isLoading" class="flex h-full w-full items-center justify-center">
          <LoadingSpinner class="size-6" />
        </div>
        <img
          v-show="!isLoading"
          :src="thumbnailImageUrl"
          :alt="modelValue.file.originalName"
          class="h-full w-full rounded-md object-cover"
          @load="onImageLoad"
          @error="onImageError"
        />
        <transition name="fade">
          <div
            v-if="modelValue.file.isPdfDocument() && !isMouseOver"
            class="surface absolute rounded-br-md pt-3 pr-2 pb-2 pl-3 opacity-80"
          >
            <FontAwesomeIcon :icon="getIcon(modelValue.file)" size="3x" />
          </div>
        </transition>
      </template>
      <div v-else class="flex w-full flex-col items-center justify-center space-y-2 p-2">
        <FontAwesomeIcon :icon="getIcon(modelValue.file)" size="3x" class="mb-8" />
        <div class="text-2xs max-w-full truncate break-all">
          {{ modelValue.file.originalName }}
        </div>
      </div>
    </div>
    <div class="flex">
      <ContextMenuWrapper
        v-if="contextMenuOpen && modelValue"
        @close-menu="contextMenuOpen = false"
      >
        <FilePreviewTileContextMenu
          v-model="modelValue"
          :delete-disabled-reason="deleteDisabledReason"
          @reload-preview="$emit('reload')"
          @delete="$emit('delete', $event)"
        />
      </ContextMenuWrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch } from "vue"
import { FileRelation } from "@/models/file/file-relation.model"
import { useDragStore } from "@/store/drag-store"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import FilePreviewTileContextMenu from "@/components/file/FilePreviewTileContextMenu.vue"
import ContextMenuWrapper from "@/components/context_menu/ContextMenuWrapper.vue"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import { vOnLongPress } from "@/directives/custom-on-long-press"
import { File as FileModel } from "@/models/file/file.model"
import FilePreviewTile from "@/components/file/FilePreviewTile.vue"

const modelValue = defineModel<FileRelation>({ required: false })

const props = withDefaults(
  defineProps<{
    dragDefaultIsCopy?: boolean
    allowContextMenu?: boolean
    deleteDisabledReason?: string
  }>(),
  {
    dragDefaultIsCopy: false,
    allowContextMenu: true
  }
)
const emit = defineEmits(["reload", "delete", "open", "moved"])

const dragStore = useDragStore()

const draggableElement = ref<HTMLDivElement | null>(null)

const isLoading = ref(true)
const hasError = ref(false)

const isMouseOver = ref(false)

const thumbnailImageUrl = computed(() => {
  const derivedFile = modelValue.value?.file.getDerivedFile()
  const thumbUrl = derivedFile?.thumbUrl ?? modelValue.value?.file.thumbUrl
  if (thumbUrl) {
    // Use the updatedAt timestamp of the derived file or the original file as cache key.
    // This ensures that the image is reloaded when the file is updated.
    const cacheKey = (derivedFile?.updatedAt ?? modelValue.value?.file.updatedAt)?.valueOf() ?? ""
    return `${thumbUrl}?${cacheKey}`
  }
  return undefined
})

const instance = getCurrentInstance()

function onDragStart(e: DragEvent) {
  if (!modelValue.value) return
  e.dataTransfer?.setData("fileRelationUuid", modelValue.value.id!)

  const isMac = navigator.userAgent.indexOf("Mac") > -1
  const isWin = navigator.userAgent.indexOf("Win") > -1
  const isCopyOperation = props.dragDefaultIsCopy || (isWin && e.ctrlKey) || (isMac && e.altKey)

  e.dataTransfer?.setData("intention", isCopyOperation ? "copy" : "move")

  dragStore.registerDrag(modelValue.value, instance as unknown as typeof FilePreviewTile)
}

function onDragEnd() {
  dragStore.unregisterDrag()
}

function onTouchStart() {
  const dragEvent = new DragEvent("dragstart", { dataTransfer: new DataTransfer() })
  draggableElement.value?.dispatchEvent(dragEvent)
}

function onTouchEnd() {
  const dragEvent = new DragEvent("dragend", {})
  draggableElement.value?.dispatchEvent(dragEvent)
}

function getIcon(file?: FileModel) {
  if (!file) return ["fas", "file"]
  if (file.isWordDocument()) {
    return ["fas", "file-word"]
  } else if (file.isSpreadsheet()) {
    return ["fas", "file-excel"]
  } else if (file.isPdfDocument()) {
    return ["fas", "file-pdf"]
  }
  return ["fas", "file"]
}

const contextMenuOpen = ref(false)

watch(contextMenuOpen, (newValue) => {
  emit("open", newValue)
})

// When we have no thumbnail url fallback to thumb-less preview.
watch(
  thumbnailImageUrl,
  (newThumbnailImageUrl) => {
    if (newThumbnailImageUrl) {
      isLoading.value = true
      hasError.value = false
    } else {
      hasError.value = true
      isLoading.value = false
    }
  },
  { immediate: true }
)

function onImageLoad() {
  isLoading.value = false
}

function onImageError() {
  hasError.value = true
  isLoading.value = false
}

function onMoveSuccessful() {
  emit("moved")
}

defineExpose({ onMoveSuccessful })
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-out; /* Quick transition duration */
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 0.8;
}
</style>
